import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../../context/NotificationContext';
import { retailerAPI } from '../../../utils/api';

const RetailerDashboard = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalSales: 0,
    pendingOrders: 0,
    revenue: 0
  });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportsModal, setShowReportsModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, productsRes] = await Promise.all([
        retailerAPI.getStats(),
        retailerAPI.getProducts()
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data || stats);
      }

      if (productsRes.data?.success) {
        setProducts(productsRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const updateProductStock = async (productId, newStock) => {
    try {
      const res = await retailerAPI.updateStock(productId, parseInt(newStock));
      if (res.data?.success) {
        showSuccess('Stock updated successfully');
        fetchDashboardData(); // Refresh data
      } else {
        showError(res.data?.message || 'Failed to update stock');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update stock');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-yellow-500">Retailer Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Products</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.totalProducts}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Sales</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.totalSales}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Pending Orders</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.pendingOrders}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Revenue</h3>
          <p className="text-2xl font-bold text-yellow-500">₹{stats.revenue}</p>
        </div>
      </div>

      {/* Product Inventory */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Product Inventory</h2>
        <div className="space-y-4">
          {products.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No products in inventory</p>
            </div>
          ) : (
            products.map((product) => (
              <div key={product._id} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 backdrop-blur-lg rounded-xl">
                <div>
                  <h3 className="font-medium">{product.name}</h3>
                  <p className="text-sm text-gray-400">Price: ₹{product.price}</p>
                  <p className="text-sm text-gray-400">Description: {product.description}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Stock:</label>
                    <input
                      type="number"
                      defaultValue={product.stock_quantity || 0}
                      onChange={(e) => updateProductStock(product._id, e.target.value)}
                      className="w-20 bg-gray-600 text-light rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <button onClick={() => showError('Please use the central product registry to edit items')} className="bg-yellow-500 text-dark px-3 py-2 rounded-lg text-sm font-medium">
                    Edit
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button onClick={() => navigate('/dashboard/retailer/products')} className="bg-yellow-500 text-dark py-3 px-4 rounded-lg font-medium hover:opacity-90">
            Add Product
          </button>
          <button
            onClick={() => navigate('/dashboard/retailer/orders')}
            className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600"
          >
            View Orders
          </button>
          <button onClick={() => setShowReportsModal(true)} className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600">
            View Reports
          </button>
        </div>
      </div>

      {/* Custom Reports Modal */}
      {showReportsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-2xl w-full p-6 max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-yellow-500">Sales & Inventory Summary</h3>
              <button onClick={() => setShowReportsModal(false)} className="text-gray-400 hover:text-white">✕</button>
            </div>

            <div className="flex-1 overflow-y-auto pr-2">
              <h4 className="font-semibold text-white mb-3">Low Stock Alerts</h4>
              <div className="space-y-3 mb-6">
                {products.filter(p => (p.stock_quantity || 0) < 10).length === 0 ? (
                  <div className="text-gray-400 text-sm bg-gray-900 p-3 rounded">All products are well stocked.</div>
                ) : (
                  products
                    .filter(p => (p.stock_quantity || 0) < 10)
                    .sort((a, b) => (a.stock_quantity || 0) - (b.stock_quantity || 0))
                    .slice(0, 5)
                    .map(p => (
                      <div key={p._id} className="flex justify-between items-center bg-gray-700 p-3 rounded-lg border-l-4 border-yellow-400">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-yellow-400 font-bold">{p.stock_quantity || 0} left</span>
                      </div>
                    ))
                )}
              </div>

              <h4 className="font-semibold text-white mb-3">Performance Overview</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-900 p-4 rounded-lg text-center flex flex-col items-center justify-center">
                  <span className="text-3xl mb-2">📦</span>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Avg Order Value</p>
                  <p className="text-xl font-bold text-white mt-1">₹{stats.totalSales > 0 ? Math.round(stats.revenue / stats.totalSales) : 0}</p>
                </div>
                <div className="bg-gray-900 p-4 rounded-lg text-center flex flex-col items-center justify-center">
                  <span className="text-3xl mb-2">⭐</span>
                  <p className="text-gray-400 text-xs uppercase tracking-wider">Top Product</p>
                  <p className="text-sm font-bold text-white mt-1 truncate w-full px-2">
                    {products.length > 0 ? products[0]?.name : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 text-right border-t border-gray-700 pt-4">
              <button onClick={() => setShowReportsModal(false)} className="bg-gray-700 hover:bg-gray-600 text-light py-2 px-6 rounded-lg font-medium transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetailerDashboard;
